/*
 * ----------------------------------------------------------------------------
 * ZAHNHELD LADEANIMATION (PRELOADER) - sitewide capable
 * ----------------------------------------------------------------------------
 * Companion to sections/zh-preloader.liquid. Deliberately ENGINE FREE: every
 * motion is a CSS transition declared in that section's stylesheet, this file
 * only flips classes and owns the timing. That is why the curtain also works on
 * templates where the homepage Motion bundle (assets/motion.js, 139 KB) is not
 * loaded, and why a blocked engine can never leave the page covered.
 *
 * The one thing CSS cannot do here is measure an SVG path, so the signature
 * start state is armed in JS (see armSignature).
 *
 * ONE CLOCK. Every step is scheduled against `cfg.t0`, the timestamp the section
 * stamps pre-paint at the same moment it reveals the curtain, and NOT against
 * the moment this file happens to execute. Those used to be two clocks: the CSS
 * failsafe ran from first paint while this timeline ran from script execution,
 * and because the script is loaded from the body it only executed once the
 * deferred head scripts were done. Measured on a throttled mobile profile that
 * gap was 1.5s on LTE and 9.2s on slow 4G, so the failsafe faded the curtain
 * while the peel was still travelling, or the whole animation started seconds
 * after the failsafe had already cleared the screen. The script is loaded async
 * for the same reason: the timeline must not queue behind motion.js.
 *
 * Handshake with assets/zh-home-motion.js:
 *   The section sets window.zhPreloader = { pending: true } pre-paint. This file
 *   dispatches `zh:preloader:done` on document when the hero should start, sets
 *   pending = false, and does so exactly once on EVERY exit path including all
 *   failsafes, so runIntro() can never be stranded behind a curtain that failed.
 *
 * Failsafes, in order of who fires first:
 *   1. this file, on the normal timeline
 *   2. the too-late guard: if the peel should already have started before this
 *      file even ran, the curtain is torn down at once instead of played late
 *   3. the 3s scroll unlock backstop (never trap scrolling)
 *   4. the pure CSS keyframe, whose animationend ALSO completes the handshake so
 *      the hero can never sit hidden behind an already invisible curtain
 */
(function () {
  'use strict';

  var HARD_UNLOCK_MS = 3000;

  var root = document.documentElement;
  var cfg = window.zhPreloaderConfig || {};

  // Beat before the centre element appears. Comes from the section so the CSS
  // failsafe delays, which are computed from the same value, cannot drift.
  var REVEAL_DELAY_MS = cfg.leadMs || 150;

  /* ---------- shared clock ---------- */

  // Stamped by the pre-paint gate in sections/zh-preloader.liquid. The fallback
  // keeps an old cached section markup working: without a stamp the best
  // available origin is the navigation itself, which is still closer to the
  // curtain's first paint than this script's execution time.
  var t0 = typeof cfg.t0 === 'number' ? cfg.t0 : 0;

  function since() {
    return performance.now() - t0;
  }

  // Schedule fn for an absolute point on the shared clock. Anything already in
  // the past runs on the next tick rather than being dropped.
  function at(target, fn) {
    setTimeout(fn, Math.max(0, target - since()));
  }

  /* ---------- handshake ---------- */

  var heroSignalled = false;

  function signalHero() {
    if (heroSignalled) return;
    heroSignalled = true;
    if (window.zhPreloader) window.zhPreloader.pending = false;
    document.dispatchEvent(new CustomEvent('zh:preloader:done'));
  }

  var scrollLocked = false;

  function unlockScroll() {
    if (!scrollLocked) return;
    scrollLocked = false;
    document.body.style.overflow = '';
  }

  function lockScroll() {
    scrollLocked = true;
    document.body.style.overflow = 'hidden';
    setTimeout(unlockScroll, HARD_UNLOCK_MS);
  }

  var el = null;

  function removeCurtain() {
    if (el && el.parentNode) el.parentNode.removeChild(el);
    el = null;
    root.classList.remove('zh-preloading');
    unlockScroll();
  }

  // Only the exit paths use this. On the normal timeline the hero is signalled
  // separately, after the curtain has left plus the configured pause.
  function teardown() {
    removeCurtain();
    signalHero();
  }

  /* ---------- signature ---------- */

  // Arm the dash start state with transitions OFF. Setting the offset while the
  // transition is live animates 0 -> full length first, so the signature would sit
  // there fully drawn and then un-draw itself.
  function armSignature() {
    var sig = document.getElementById('zhp-sig');
    if (!sig) return;
    var paths = Array.prototype.slice.call(sig.querySelectorAll('path'));
    var drawMs = cfg.drawMs || 1100;

    paths.forEach(function (p) {
      var len;
      try {
        len = p.getTotalLength();
      } catch (e) {
        len = 1000;
      }
      p.style.transition = 'none';
      p.style.strokeDasharray = len;
      p.style.strokeDashoffset = len;
    });

    // Flush the armed state, then hand the property back to the CSS transition.
    void sig.getBoundingClientRect().width;

    paths.forEach(function (p, i) {
      p.style.transition = '';
      // Sub paths draw one after another, which reads as a natural pen lift.
      p.style.transitionDelay = i === 0 ? '0ms' : Math.round(drawMs * 0.92) + 'ms';
      if (i > 0) p.style.transitionDuration = Math.round(drawMs * 0.12) + 'ms';
    });

    return sig;
  }

  /* ---------- run ---------- */

  function run() {
    // Timeline, all points absolute on the shared clock.
    var revealAt = cfg.mode === 'wordmark' ? 0 : REVEAL_DELAY_MS;
    var curtainStart;
    var curtainMs;

    if (cfg.mode === 'wordmark') {
      curtainStart = cfg.holdMs;
      curtainMs = cfg.curtainMs;
    } else {
      curtainStart = REVEAL_DELAY_MS + (cfg.revealMs || 500) + (cfg.peelHoldMs || 0);
      curtainMs = cfg.peelMs;
    }

    var curtainGone = curtainStart + curtainMs;
    var heroAt = curtainGone + (cfg.heroDelayMs || 0);

    // Too late to play. Showing the centre element now and immediately peeling it
    // away reads as a glitch, so the curtain simply leaves and the page is freed.
    // This is the branch a slow connection takes.
    if (since() >= curtainStart) {
      teardown();
      return;
    }

    try {
      if (cfg.oncePerSession !== false) sessionStorage.setItem('zh:preloaded', '1');
    } catch (e) {
      /* the gate already handled blocked storage */
    }

    lockScroll();

    var sig = cfg.mode === 'signature' ? armSignature() : null;

    // The CSS failsafe giving up must also release the hero, otherwise the page
    // sits behind an invisible curtain with every intro unit still hidden.
    el.addEventListener('animationend', function (e) {
      if (e.animationName === 'zhp-failsafe') teardown();
    });

    at(revealAt, function () {
      if (!el) return;
      // Two frames: the armed start state has to be painted before the class flip,
      // otherwise the browser has nothing to transition from.
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          if (!el) return;
          el.classList.add('is-in');
          if (sig) sig.classList.add('is-drawn');
        });
      });
    });

    at(curtainStart, function () {
      if (el) el.classList.add('is-out');
      // Free the page the moment the curtain starts moving, not when it is gone.
      unlockScroll();
    });

    // Hero media settles from its zoom while the curtain is still travelling. Pure
    // CSS as well, the initial scale sits in assets/zh-home-motion.css.
    at(curtainStart + 100, function () {
      var media = document.querySelector('[data-zhm="hero-media"]');
      if (media) media.classList.add('is-settled');
    });

    at(curtainGone, removeCurtain);
    at(heroAt, signalHero);
    // Belt and braces, derived from the timeline instead of a fixed value: a hard
    // 3.5s would have signalled the hero mid curtain on long configurations.
    at(heroAt + 1200, signalHero);
  }

  /* ---------- start ---------- */

  // The gate decided against a curtain, so there is nothing to play and nothing
  // to wait for: hand off at once so the hero intro is never blocked.
  if (!root.classList.contains('zh-preloading')) {
    teardown();
    return;
  }

  // Loaded async, so this file can execute before the parser has reached the
  // curtain markup. Waiting for the node is what makes async safe here; without
  // it an early execution would take the "nothing to play" path and the curtain
  // would never run at all.
  el = document.getElementById('zhp');
  if (el) {
    run();
  } else {
    document.addEventListener(
      'DOMContentLoaded',
      function () {
        el = document.getElementById('zhp');
        if (el) run();
        else teardown();
      },
      { once: true },
    );
  }
})();
